import React from "react";
import { HelpCircle, ExternalLink, FileText } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { GenericTable } from "@shared/components/GenericTable";
import { useSopTabDetails } from "../../../../hooks";

interface HelpTabProps {
  sopDetails: any;
}

interface Document {
  id: number;
  type: number;
  typeDisplay: string;
  sop: number;
  remoteUtilityId: number;
  file: string | null;
  link: string | null;
  description: string;
  isActive: boolean;
}

const HelpTab: React.FC<HelpTabProps> = ({ sopDetails }) => {
  // Fetch help documents using the API
  const {
    data: helpData,
    isLoading: helpLoading,
    error: helpError,
  } = useSopTabDetails({
    id: sopDetails.id.toString(),
    remoteUtilityId: sopDetails.remoteUtilityId?.toString(),
    fieldName: "help",
  });

  // Extract documents from the API response and add index for row identification
  const documents: (Document & { index: number })[] = (
    helpData?.result?.documents || []
  )
    .filter((doc: Document) => doc.isActive)
    .map((document: Document, index: number) => ({
      ...document,
      index,
    }));

  // Render function for document links
  const renderDocumentLink = (document: Document) => {
    if (document.file) {
      return (
        <a
          href={document.file}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FileText className="h-4 w-4 mr-1" />
          View File
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    if (document.link) {
      return (
        <a
          href={document.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Open Link
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    return <span className="text-muted-foreground">No link/file</span>;
  };

  // GenericTable column configuration
  const tableColumns = [
    {
      key: "index",
      header: "Sr. No",
      render: (value: number) => (
        <span className="font-medium">{value + 1}</span>
      ),
    },
    {
      key: "typeDisplay",
      header: "Type",
      render: (value: string) => (
        <Badge variant="outline" className="capitalize">
          {value}
        </Badge>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (value: string) => (
        <span className="text-gray-900">
          {value || (
            <span className="text-muted-foreground">No description</span>
          )}
        </span>
      ),
    },
    {
      key: "link",
      header: "Resource",
      render: (value: string, row: Document) => renderDocumentLink(row),
    },
  ];

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center pb-2">
              <HelpCircle className="mr-2 h-5 w-5 text-primary" />
              Help Resources
            </CardTitle>
            <CardDescription>
              Resources to help technicians with this procedure
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {helpLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-muted-foreground">
                Loading help resources...
              </span>
            </div>
          ) : helpError ? (
            <div className="text-center py-8 text-destructive">
              Error loading help resources: {helpError.message}
            </div>
          ) : (
            <GenericTable
              data={documents}
              columns={tableColumns}
              rowKey="id"
              emptyMessage="No help resources defined for this SOP"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpTab;
